import { useEffect, useState } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { BookMarked, Compass, FlaskConical, Home, Menu, Moon, NotebookPen, Search, Settings, SunMedium, Swords, X } from 'lucide-react';
import { Button, Shortcut, StatusBadge } from '@cpu/design-system';
import { CommandPalette } from './CommandPalette';
import { useTheme } from './theme';
import { CelebrationLayer } from './celebrate';
import { FocusProvider, useFocus } from './focusTimer';
import { ShortcutSheet, useKeyboardLayer } from './shortcuts';
import { ToastProvider } from './toast';

const navigation = [
  { to: '/', label: 'Today', icon: Home },
  { to: '/atlas', label: 'Atlas', icon: Compass },
  { to: '/learn', label: 'Labs', icon: FlaskConical },
  { to: '/practice', label: 'Practice', icon: Swords },
  { to: '/review', label: 'Review', icon: BookMarked }
] as const;

export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const { theme, cycleTheme } = useTheme();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    addEventListener('online', update);
    addEventListener('offline', update);
    return () => { removeEventListener('online', update); removeEventListener('offline', update); };
  }, []);
  useEffect(() => setDrawerOpen(false), [pathname]);

  return (
    <ToastProvider>
      <FocusProvider>
        <ShellChrome
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          online={online}
          theme={theme}
          cycleTheme={cycleTheme}
          pathname={pathname}
        />
      </FocusProvider>
    </ToastProvider>
  );
}

function ShellChrome({ drawerOpen, setDrawerOpen, online, theme, cycleTheme, pathname }: {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  online: boolean;
  theme: string;
  cycleTheme: () => void;
  pathname: string;
}) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { sheetOpen, setSheetOpen } = useKeyboardLayer(() => setPaletteOpen(true));

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setDrawerOpen(false); };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [drawerOpen, setDrawerOpen]);

  return (
    <div className="app-shell">
      <header className="global-header">
        <Button variant="quiet" iconOnly aria-label="Open navigation" onClick={() => setDrawerOpen(true)}><Menu size={20} /></Button>
        <Link to="/" className="brand-lockup" aria-label="CP Ultimate Trace home">
          <span className="brand-mark">CP</span><span><b>Ultimate</b><small>TRACE</small></span>
        </Link>
        <nav className="desktop-nav" aria-label="Primary navigation">
          {navigation.map(({ to, label }) => <Link key={to} to={to} activeProps={{ className: 'is-active' }}>{label}</Link>)}
        </nav>
        <div className="header-actions">
          {!online && <StatusBadge>Offline</StatusBadge>}
          <button className="command-trigger" onClick={() => setPaletteOpen(true)} aria-label="Search or act (Ctrl K)">
            <Search size={16} /><span>Search or act</span><Shortcut>Ctrl K</Shortcut>
          </button>
          <button className="settings-link theme-toggle" aria-label="Cycle theme" onClick={cycleTheme}>
            {theme === 'paper' ? <Moon size={18} /> : <SunMedium size={18} />}
          </button>
          <Link to="/settings" className="settings-link" aria-label="Settings and comfort controls"><Settings size={18} /></Link>
        </div>
      </header>
      <ReadingProgressBar />

      <div className={`nav-scrim ${drawerOpen ? 'is-open' : ''}`} onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      <aside className={`navigation-drawer ${drawerOpen ? 'is-open' : ''}`} aria-hidden={!drawerOpen} aria-label="Navigation drawer">
        <div className="drawer-head"><span>Navigate</span><Button variant="quiet" iconOnly aria-label="Close navigation" onClick={() => setDrawerOpen(false)}><X size={19} /></Button></div>
        <nav aria-label="Drawer navigation">
          {navigation.map(({ to, label, icon: Icon }) => <Link key={to} to={to} tabIndex={drawerOpen ? 0 : -1}><Icon size={18} />{label}</Link>)}
          <Link to="/playbook" tabIndex={drawerOpen ? 0 : -1}><BookMarked size={18} />Method</Link>
          <Link to="/notes" tabIndex={drawerOpen ? 0 : -1}><NotebookPen size={18} />Note library</Link>
          <Link to="/settings" tabIndex={drawerOpen ? 0 : -1}><Settings size={18} />Settings</Link>
        </nav>
        <div className="drawer-mission">
          <small>Interactive layer</small>
          <b>Twenty-five Trace Labs online</b>
          <Link to="/learn" tabIndex={drawerOpen ? 0 : -1}>Browse the labs →</Link>
        </div>
      </aside>

      <main className="route-viewport" key={pathname}><Outlet /></main>

      <nav className="mobile-tabbar" aria-label="Mobile navigation">
        {navigation.map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} activeProps={{ className: 'is-active' }}><Icon size={20} aria-hidden="true" /><span>{label}</span></Link>
        ))}
      </nav>

      <FocusDock paletteOpen={paletteOpen} setPaletteOpen={setPaletteOpen} sheetOpen={sheetOpen} setSheetOpen={setSheetOpen} />
      <CelebrationLayer />
    </div>
  );
}

/** Mounts the palette + shortcut sheet inside providers that need them. */
function FocusDock({ paletteOpen, setPaletteOpen, sheetOpen, setSheetOpen }: {
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
}) {
  return (
    <>
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
      <ShortcutSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}

/** Thin scroll-progress bar pinned under the header. */
function ReadingProgressBar() {
  const [bar, setBar] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!bar) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight;
      bar.style.transform = `scaleX(${max > 0 ? doc.scrollTop / max : 0})`;
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => { removeEventListener('scroll', onScroll); if (frame) cancelAnimationFrame(frame); };
  }, [bar]);
  return <div className="reading-progress" aria-hidden ref={setBar} />;
}
